/**
 * Single source of truth for campaign-detail user-facing strings.
 * Shape is i18n-ready (string keys + interpolation args), so a later
 * wave can drop in `react-i18next` without rewriting callers.
 *
 * UX-copy review rules applied: short, action-first, no jargon, no
 * empty fallbacks, consistent past-tense for confirmations + present-
 * tense for actions.
 */

import type { RollbackKind, RollbackReasonCode } from '@discovr/types';
import { CONFIRM_WORD } from '../campaign-detail/confirmWord';

// Sentence-case stage names used by the rollback menu, dialog and activity line.
// live_pending matches the row badge ("Awaiting URL").
const ROLLBACK_STAGE_LABELS = {
  content_pending: 'Content pending',
  live_pending: 'Awaiting URL',
  script_review: 'Script review',
  content_review: 'Content review',
  confirmed: 'Confirmed',
  accepted_by_brand: 'Brand approved',
  pending: 'Pending',
} as const;

const APPROVAL_ROLLBACK_STAYS = 'all versions, AI analysis and the earlier approval in the activity log.';

const ROLLBACK_KINDS = {
  script: {
    from: ROLLBACK_STAGE_LABELS.content_pending,
    to: ROLLBACK_STAGE_LABELS.script_review,
    body: 'Use this when the wrong script was approved. The script goes back to review so the brand can approve it again, or you can upload a corrected version.',
    changes: 'status returns to Script review · the script thread reopens · the content ETA is cleared.',
    stays: APPROVAL_ROLLBACK_STAYS,
  },
  content: {
    from: ROLLBACK_STAGE_LABELS.live_pending,
    to: ROLLBACK_STAGE_LABELS.content_review,
    body: "Use this when the wrong content was approved. The content goes back to review so the brand can approve it again, or you can upload a corrected version. This doesn't remove anything the creator has already published.",
    changes: 'status returns to Content review · the content thread reopens · the live ETA is cleared.',
    stays: APPROVAL_ROLLBACK_STAYS,
  },
  acceptance: {
    from: ROLLBACK_STAGE_LABELS.confirmed,
    to: ROLLBACK_STAGE_LABELS.accepted_by_brand,
    body: 'Use this when a creator was confirmed by mistake and nothing has been uploaded yet. The creator goes back to Brand approved and you can approve them again later.',
    changes:
      'status returns to Brand approved · the production record (nothing was uploaded), its ETAs and the confirmed spend are deleted · the brief-sent mark is cleared, so the brief must be sent again.',
    stays: 'the selected deliverables, the selection thread and the full activity history.',
  },
  brand_approval: {
    from: ROLLBACK_STAGE_LABELS.accepted_by_brand,
    to: ROLLBACK_STAGE_LABELS.pending,
    body: 'Use this when the brand needs to choose deliverables again. The creator goes back to Pending for a fresh selection.',
    changes:
      'status returns to Pending · the selected deliverables are cleared so the brand selects again · the estimate resets · the brief-sent mark is cleared.',
    stays:
      'the selection thread, the full activity history and the product assignments, which come back when the creator is confirmed again.',
  },
} as const satisfies Record<RollbackKind, Record<'from' | 'to' | 'body' | 'changes' | 'stays', string>>;

type RollbackReasonOption = { readonly code: RollbackReasonCode; readonly label: string };

const APPROVED_BY_MISTAKE: RollbackReasonOption = { code: 'approved_by_mistake', label: 'Approved by mistake' };
const COMPLIANCE_ISSUE: RollbackReasonOption = { code: 'compliance_issue', label: 'Compliance or disclosure issue' };
const OFF_BRIEF: RollbackReasonOption = { code: 'off_brief_or_brand_safety', label: 'Off-brief or brand-safety issue' };
const OTHER_REASON: RollbackReasonOption = { code: 'other', label: 'Other' };

// Used when a row's rollback kind is unknown, so a shared code is never labelled for the wrong stage.
const NEUTRAL_REASON_LABELS: Partial<Record<RollbackReasonCode, string>> = { needs_changes: 'Needs changes' };

// Each rollback offers only its own subset (the backend rejects any other code).
const ROLLBACK_REASONS: Record<RollbackKind, ReadonlyArray<RollbackReasonOption>> = {
  acceptance: [
    APPROVED_BY_MISTAKE,
    { code: 'wrong_creator', label: 'Wrong creator confirmed' },
    { code: 'brief_changed', label: 'Brand changed the brief or terms' },
    { code: 'creator_unavailable', label: 'Creator unavailable' },
    OTHER_REASON,
  ],
  brand_approval: [
    { code: 'wrong_deliverables', label: 'Wrong deliverables selected' },
    { code: 'scope_or_budget_changed', label: 'Scope or budget changed' },
    { code: 'brand_requested_change', label: 'Brand requested a change' },
    APPROVED_BY_MISTAKE,
    OTHER_REASON,
  ],
  script: [
    { code: 'needs_changes', label: 'Script needs changes' },
    COMPLIANCE_ISSUE,
    OFF_BRIEF,
    { code: 'claims_unsubstantiated', label: 'Claims need substantiation' },
    APPROVED_BY_MISTAKE,
    OTHER_REASON,
  ],
  content: [
    { code: 'needs_changes', label: 'Content needs changes' },
    COMPLIANCE_ISSUE,
    { code: 'music_or_licensing', label: 'Music or licensing issue' },
    OFF_BRIEF,
    APPROVED_BY_MISTAKE,
    OTHER_REASON,
  ],
};

const TYPED_CONFIRM_BEFORE = 'Type';
const TYPED_CONFIRM_AFTER = 'to continue';

export const campaignCopy = {
  typedConfirm: {
    labelBefore: TYPED_CONFIRM_BEFORE,
    labelAfter: TYPED_CONFIRM_AFTER,
    prompt: `${TYPED_CONFIRM_BEFORE} ${CONFIRM_WORD} ${TYPED_CONFIRM_AFTER}`,
  },
  brief: {
    upload: {
      success: 'Brief uploaded',
      error: 'Could not upload brief',
    },
    notify: {
      success: 'Brief notification sent',
      error: 'Could not send brief notification',
    },
  },
  script: {
    submit: {
      success: 'Script saved',
      error: 'Could not save script',
    },
    approve: {
      success: 'Script approved',
      error: 'Could not approve script',
    },
    aiReview: {
      success: 'AI script review updated',
      error: 'AI script review failed',
    },
  },
  content: {
    submit: {
      success: 'Content saved',
      error: 'Could not save content',
    },
    approve: {
      success: 'Content approved',
      error: 'Could not approve content',
    },
    aiReview: {
      success: 'AI content review updated',
      error: 'AI content review failed',
    },
  },
  creator: {
    approve: {
      success: 'Creator approved',
      error: 'Could not approve creator',
    },
    reject: {
      success: 'Creator rejected',
      error: 'Could not reject creator',
    },
    bulkApprove: {
      error: 'Could not approve the selected creators',
      noneApproved: 'No creators were approved',
      pastVerb: 'Approved',
      /** Shown when a bulk approval is attempted while selected creators have no commercial. */
      needsCommercial: (count: number) =>
        count === 1
          ? 'Select a commercial for 1 creator before approving'
          : `Select a commercial for ${count} creators before approving`,
      /** Shown when approving on behalf of the brand while selected creators' profiles can't be offered. */
      notOfferable: (count: number) =>
        count === 1
          ? "1 selected creator's profile can't be offered to the brand"
          : `${count} selected creators' profiles can't be offered to the brand`,
      /** Names the creators blocking a bulk approval, as they may sit on another page. */
      blockingCreators: (listed: readonly string[], hiddenCount: number) =>
        hiddenCount > 0 ? `${listed.join(', ')} and ${hiddenCount} more` : listed.join(', '),
      /** Confirm dialog copy, the plural of the single-row approve dialogs. */
      dialog: {
        brandApprove: {
          title: (count: number) => (count === 1 ? 'Approve 1 creator' : `Approve ${count} creators`),
          description:
            'Each creator is approved with the deliverables shown. This cannot be changed after approval.',
        },
        approveAsBrand: {
          title: (count: number) =>
            count === 1 ? 'Approve 1 creator on behalf of brand?' : `Approve ${count} creators on behalf of brand?`,
          description:
            "You're recording a brand approval for these creators with the deliverables shown. Nothing is sent until you confirm.",
        },
        missingMain: (count: number) =>
          count === 1
            ? '1 creator needs a main deliverable before you can confirm'
            : `${count} creators need a main deliverable before you can confirm`,
      },
    },
    bulkDelete: {
      error: 'Could not delete the selected creators',
      noneDeleted: 'No creators were deleted',
      pastVerb: 'Deleted',
    },
    bulkSelection: {
      /** Shown when a click would take the selection past what the API accepts. */
      tooMany: (max: number) => `You can select up to ${max} creators at a time`,
      /** Shown when a select-all only fits part of the page under the cap. */
      partlyAdded: (added: number, max: number) =>
        added === 1
          ? `Only 1 more creator was selected. You can select up to ${max} at a time`
          : `Only ${added} more creators were selected. You can select up to ${max} at a time`,
      /** Shown when a refresh removes selected creators that are no longer on the page. */
      autoDeselected: (count: number) =>
        count === 1
          ? '1 selected creator is no longer on this page and was deselected'
          : `${count} selected creators are no longer on this page and were deselected`,
    },
    delete: {
      success: 'Creator deleted',
      error: 'Could not delete creator',
    },
    reinstate: {
      success: 'Creator moved back to Pending.',
      error: 'Could not reinstate this creator',
    },
    hold: {
      success: 'Post put on hold.',
      error: 'Could not put this post on hold',
      liveBlocked: "Can't put on hold once content is live",
      notAvailable: "Can't put on hold at this stage",
      alreadyOnHold: 'Already on hold',
      actionUnavailable: 'Not available yet',
      heldActionReason: 'This post is on hold',
      badge: 'On hold',
      menu: {
        trigger: 'More actions',
        hold: 'Put on hold',
        release: 'Release hold',
      },
      emptyState: {
        title: 'No creators on hold',
        body: 'Creators put on hold show here until the hold is released.',
      },
      details: {
        label: 'Hold details',
        returnsTo: 'Returns to:',
        heldAt: 'Held',
        history: 'History',
        historyLabel: 'Hold history',
        held: 'Held',
        released: 'Released',
      },
      note: {
        placeholder: 'Add context for the next admin',
        labelRequired: 'Note (required)',
        labelOptional: 'Note (optional)',
        required: 'Add a note when the reason is Other',
        tooLong: (max: number) => `Note must be ${max} characters or fewer`,
        remaining: (count: number) => `${count} characters left`,
      },
      dialog: {
        title: (name: string) => `Put ${name} on hold?`,
        description: 'This post is read-only and hidden from the brand until the hold is released.',
        reasonLabel: 'Reason',
        reasonPlaceholder: 'Select a reason',
        reasonRequired: 'Select a reason',
        confirm: 'Put on hold',
        confirmHelper: "The brand won't see this creator until you release the hold.",
        cancel: 'Cancel',
        fallbackName: 'this creator',
      },
      releaseDialog: {
        title: (name: string) => `Release hold on ${name}?`,
        description: 'The post returns to the exact stage it was in and becomes visible to the brand again.',
        confirm: 'Release hold',
      },
    },
    release: {
      success: 'Hold released.',
      error: 'Could not release this hold',
    },
    previousProducts: {
      prompt: (names: string) => `Last time: ${names}. Keep these or change them?`,
      keep: 'Keep these',
      change: 'Change',
      keepError: 'Could not keep these products',
      locked: 'Products locked',
      noProductsOnApproval: "You're approving this script, but no product is assigned to this creator.",
    },
    rollback: {
      success: {
        content_pending: 'Rolled back to Script review.',
        live_pending: 'Rolled back to Content review.',
        acceptance: 'Rolled back to Brand approved.',
        brand_approval: 'Rolled back to Pending.',
      },
      error: 'Could not roll back this approval',
      errorCreator: 'Could not roll back this creator',
      stageChanged: "This creator's status has changed. Reload and try again.",
      notRollbackable: 'This creator can no longer be rolled back. Reload and try again.',
      stageDisabled: 'That stage is turned off for this campaign.',
      holdBlocked: 'Release the hold first',
      profileUnofferable:
        "This creator's profile isn't public or hasn't been fetched yet, so moving them back to Pending would hide them from the brand.",
      stageLabels: ROLLBACK_STAGE_LABELS,
      kinds: ROLLBACK_KINDS,
      reasons: ROLLBACK_REASONS,
      neutralReasonLabels: NEUTRAL_REASON_LABELS,
      stages: {
        content_pending: { ...ROLLBACK_KINDS.script, menu: 'Roll back (Script review)' },
        live_pending: { ...ROLLBACK_KINDS.content, menu: 'Roll back (Content review)' },
      },
      acceptanceStage: { ...ROLLBACK_KINDS.acceptance, menu: 'Roll back (Brand approved)' },
      brandApprovalStage: { ...ROLLBACK_KINDS.brand_approval, menu: 'Roll back (Pending)' },
      dialog: {
        title: (name: string, from: string, to: string) => `Roll back ${name} from ${from} to ${to}?`,
        changesLabel: 'What changes:',
        staysLabel: 'What stays:',
        brandNote: 'The brand is notified and sees a note in the thread. The reason stays internal.',
        reasonLabel: 'Why are you rolling this back?',
        reasonPlaceholder: 'Select a reason',
        reasonRequired: 'Select a reason to continue',
        noteLabel: 'Note (optional)',
        confirmLabelAfter: 'to roll back',
        confirm: 'Roll back',
        cancel: 'Cancel',
      },
      activity: {
        before: 'rolled back ',
        after: (from: string, to: string) => `'s approval from ${from} to ${to}`,
        toStage: (to: string) => ` to ${to}`,
        unknownFrom: 'a later stage',
        unknownTo: 'an earlier stage',
        reasonDetail: (label: string, note: string) => (label && note ? `${label}: ${note}` : label || note),
      },
    },
    add: {
      success: 'Creator added',
      error: 'Could not add creator',
    },
    update: {
      success: 'Creator updated',
      error: 'Could not update creator',
    },
    sync: {
      success: 'Creator metrics synced',
      error: 'Could not sync metrics',
    },
    setFinalAmount: {
      success: 'Final amount set',
      error: 'Could not set final amount',
    },
    submitSelection: {
      success: 'Creator selection submitted',
      error: 'Could not submit creator selection',
    },
    importReview: {
      preview: {
        error: "Couldn't read this sheet",
        blocked:
          "The upload didn't reach Discovr. It may have been blocked or the connection dropped. Re-save the file as a new .xlsx in Excel or Google Sheets and upload again. If it keeps failing, contact the tech team.",
      },
      mapping: { error: 'Could not save column mapping' },
      sheetResolution: { error: 'Could not update this row' },
      advance: { error: 'Could not move to roster review' },
      rosterResolution: { error: 'Could not update this row' },
      commit: { error: 'Could not start the import' },
      discard: { error: 'Could not discard this import' },
      outreachFailed: {
        heading: (count: number) => `${count} creator${count === 1 ? '' : 's'} couldn't be added to outreach`,
        retry: 'Retry failed',
        retrying: 'Retrying…',
        error: 'Could not retry this import. Try again in a moment.',
      },
    },
  },
  bid: {
    respond: {
      success: 'Response submitted',
      error: 'Could not respond to bid',
    },
    finalizeAmounts: {
      success: 'Amounts finalized',
      error: 'Could not finalize amounts',
    },
    updateStatuses: {
      success: 'Creator statuses updated',
      error: 'Could not update creator statuses',
    },
  },
  liveContent: {
    setUrl: {
      success: 'Live URL saved',
      error: 'Could not save live URL',
    },
    saveInsights: {
      success: 'Insights saved',
      error: 'Could not save insights',
    },
  },
  campaign: {
    update: {
      success: 'Campaign updated',
      error: 'Could not update campaign',
    },
  },
  thread: {
    sendMessage: {
      error: 'Could not send message',
    },
  },
} as const;
